import React, { Suspense, lazy, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ThemeProvider } from './contexts/ThemeContext';
import { EnhancedAppProvider } from './contexts/EnhancedAppContext';
import { FeatureProvider } from './contexts/FeatureContext';
import { UserPreferencesProvider } from './contexts/UserPreferencesContext';
import { HealthProvider } from './contexts/HealthContext';
import { ActivityProvider } from './contexts/ActivityContext';
import { SubscriptionProvider, SubscriptionTier } from './contexts/SubscriptionContext';
import { PaymentProvider } from './contexts/PaymentContext';
import { AdminProvider } from './contexts/AdminContext';
import { NotificationProvider } from './contexts/NotificationContext';
import { ChatProvider } from './contexts/ChatContext';
import { SafetyProvider } from './contexts/SafetyContext';
import { PerformanceProvider, usePerformance } from './contexts/PerformanceContext';
import { PendingMatchesProvider } from './contexts/PendingMatchesContext';

// Import global transition styles
import './styles/transitions.css';

// Core components
import LoadingScreen from './components/ui/LoadingScreen';
import Navigation from './components/Navigation';
import ErrorBoundary from './components/ErrorBoundary';
import RouteTracker from './components/RouteTracker';
import PWAInstallBanner from './components/PWAInstallBanner';

// SECURE Protected route component with proper Firebase Admin authentication
const AdminProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const [isLoading, setIsLoading] = React.useState(true);
  const [isAuthenticated, setIsAuthenticated] = React.useState(false);
  const { user } = useAuth(); // Get current Firebase user

  React.useEffect(() => {
    const verifyAdminAccess = async () => {
      setIsLoading(true);

      try {
        // SECURITY: Must have authenticated Firebase user
        if (!user?.uid) {
          setIsAuthenticated(false);
          setIsLoading(false);
          return;
        }

        // SECURITY: Verify admin role in Firestore (server-side validation)
        const { doc, getDoc } = await import('firebase/firestore');
        const { db } = await import('./config/firebase');

        const adminDoc = await getDoc(doc(db, 'adminUsers', user.uid));

        if (adminDoc.exists()) {
          const adminData = adminDoc.data();
          const validRoles = ['owner', 'super_admin', 'admin', 'moderator'];

          if (validRoles.includes(adminData.role) && adminData.isActive) {
            setIsAuthenticated(true);

            // SECURITY: Store minimal session info (not in localStorage for security)
            sessionStorage.setItem('admin_verified', JSON.stringify({
              timestamp: Date.now(),
              role: adminData.role,
              uid: user.uid
            }));
            
            console.log('✅ Admin verified:', adminData.role);
          } else if (!adminData.isActive) {
            console.warn('🚫 Admin account is inactive:', user.email);
            setIsAuthenticated(false);
          } else {
            console.warn('🚫 User has invalid admin role:', adminData.role);
            setIsAuthenticated(false);
          }
        } else {
          console.warn('🚫 User is not in adminUsers collection');
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error('❌ Error verifying admin access:', error);
        setIsAuthenticated(false);
      } finally {
        setIsLoading(false);
      }
    };

    verifyAdminAccess();
  }, [user?.uid]); // Re-verify when user changes

  if (isLoading) {
    return <LoadingScreen />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  return <>{children}</>;
};

// Lazy loaded screens
const LandingPage = lazy(() => import('./components/landing/LandingPage'));
const GridScreen = lazy(() => import('./screens/GridScreen'));
const ProfileScreen = lazy(() => import('./screens/ProfileScreen'));
const MessagesScreen = lazy(() => import('./components/screens/MessagesScreen'));
const ChatScreen = lazy(() => import('./components/screens/ChatScreen'));
const EditProfileScreen = lazy(() => import('./components/screens/EditProfileScreen'));
const NotificationsSettingsScreen = lazy(() => import('./components/screens/NotificationsSettingsScreen'));
const PrivacySecurityScreen = lazy(() => import('./components/screens/PrivacySecurityScreen'));
const BlockedUsersScreen = lazy(() => import('./components/screens/BlockedUsersScreen'));
const PremiumScreen = lazy(() => import('./components/screens/PremiumScreen'));
const HelpScreen = lazy(() => import('./components/screens/HelpScreen'));
const CruiseScreen = lazy(() => import('./screens/CruiseScreen'));
const TrackScreen = lazy(() => import('./screens/TrackScreen'));
const HealthScreen = lazy(() => import('./screens/HealthScreen'));
const ActivityScreen = lazy(() => import('./screens/ActivityScreen'));
const LogNewActivityScreen = lazy(() => import('./screens/LogNewActivityScreen'));
const SettingsScreen = lazy(() => import('./screens/SettingsScreen'));
const AuthScreen = lazy(() => import('./components/auth/AuthScreen'));
const OnboardingFlow = lazy(() => import('./components/onboarding/ImprovedOnboardingFlow'));

// Admin screens
const AdminLoginScreen = lazy(() => import('./screens/AdminLoginScreen'));
const AdminLayout = lazy(() => import('./layouts/AdminLayout'));
const AdminDashboard = lazy(() => import('./screens/admin/AdminDashboard'));
const UserManagement = lazy(() => import('./screens/admin/UserManagement'));
const ContentModeration = lazy(() => import('./screens/admin/ContentModeration'));
const ReportsManagement = lazy(() => import('./screens/admin/ReportsManagement'));
const CruisingLocations = lazy(() => import('./screens/admin/CruisingLocations'));
const AnalyticsDashboard = lazy(() => import('./screens/admin/AnalyticsDashboard'));
const SystemSettings = lazy(() => import('./screens/admin/SystemSettings'));
const SubscriptionConfiguration = lazy(() => import('./screens/admin/SubscriptionConfiguration'));
const MessagesManagement = lazy(() => import('./screens/admin/MessagesManagement'));
const PromptsManagement = lazy(() => import('./screens/admin/PromptsManagement'));
const PaymentDashboard = lazy(() => import('./screens/admin/PaymentDashboard'));
const FinancialDashboard = lazy(() => import('./screens/admin/FinancialDashboard'));

// Team Management Components
const TeamMembersScreen = lazy(() => import('./screens/admin/TeamMembersScreen'));
const TeamInvitesScreen = lazy(() => import('./screens/admin/TeamInvitesScreen'));
const PermissionsScreen = lazy(() => import('./screens/admin/PermissionsScreen'));

// System Administration Components
const AuditLogsScreen = lazy(() => import('./screens/admin/AuditLogsScreen'));
const SystemHealthScreen = lazy(() => import('./screens/admin/SystemHealthScreen'));

const PaymentSettingsScreen = lazy(() => import('./components/screens/PaymentSettingsScreen'));
const BugsFeedbackScreen = lazy(() => import('./components/screens/BugsFeedbackScreen'));

// User roles
export const UserRoles = {
  FREE: 'free',
  PREMIUM: 'premium',
  ADMIN: 'admin'
};

// Main App Content Component (wrapped inside providers)
const AppContent = () => {
  const { user, userProfile, isAuthenticated, isLoading } = useAuth();
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = React.useState(false);

  // Check if user has completed onboarding based on profile completeness
  React.useEffect(() => {
    if (isAuthenticated && userProfile) {
      // Check if essential profile fields are filled
      const hasBasicInfo = userProfile.displayName && userProfile.age && userProfile.bio;
      const hasPhotos = userProfile.photos && userProfile.photos.length > 0;
      const hasPreferences = userProfile.preferences; // Or check specific preference fields

      // Check for new enhanced onboarding fields
      const hasPronouns = userProfile.pronouns; // Optional but should be collected
      const hasPhysicalDetails = userProfile.nowProfile?.quickStats?.height && userProfile.nowProfile?.position;
      const hasTribes = userProfile.nowProfile?.tribes && userProfile.nowProfile.tribes.length > 0;
      const hasPrompts = userProfile.datingProfile?.prompts && userProfile.datingProfile.prompts.length >= 2;

      // Consider onboarding complete if user has all essential fields including new enhanced fields
      const completed = hasBasicInfo && hasPhotos && hasPhysicalDetails && hasPrompts;
      setHasCompletedOnboarding(completed);
    } else if (isAuthenticated && !userProfile) {
      // If authenticated but no profile exists yet, definitely need onboarding
      setHasCompletedOnboarding(false);
    }
  }, [isAuthenticated, userProfile]);

  if (isLoading) {
    return <LoadingScreen />;
  }

  const userRole = user?.role || UserRoles.FREE;

  return (
    <BrowserRouter>
      <div className="min-h-screen w-full safe-area-all">
        <Suspense fallback={<LoadingScreen />}>
          <Routes>
            {/* Admin Routes - Separate from user app */}
            <Route path="/admin/login" element={<AdminLoginScreen />} />
            <Route path="/admin/*" element={
              <AdminProtectedRoute>
                <AdminLayout />
              </AdminProtectedRoute>
            }>
              <Route path="dashboard" element={<AdminDashboard />} />
              <Route path="users" element={<UserManagement />} />
              <Route path="users/banned" element={<UserManagement />} />
              <Route path="users/reports" element={<ReportsManagement />} />
              <Route path="content/pending" element={<ContentModeration />} />
              <Route path="content/approved" element={<ContentModeration />} />
              <Route path="content/rejected" element={<ContentModeration />} />
              <Route path="reports" element={<ReportsManagement />} />
              <Route path="locations" element={<CruisingLocations />} />
              <Route path="locations/pending" element={<CruisingLocations />} />
              <Route path="locations/approved" element={<CruisingLocations />} />
              <Route path="subscriptions" element={<SubscriptionConfiguration />} />
              <Route path="subscriptions/pricing" element={<SubscriptionConfiguration />} />
              <Route path="subscriptions/limits" element={<SubscriptionConfiguration />} />
              <Route path="messages" element={<MessagesManagement />} />
              <Route path="prompts" element={<PromptsManagement />} />
              <Route path="financial" element={<FinancialDashboard />} />
              <Route path="financial/revenue" element={<FinancialDashboard />} />
              <Route path="financial/forecasting" element={<FinancialDashboard />} />
              <Route path="payments" element={<PaymentDashboard />} />
              <Route path="analytics" element={<AnalyticsDashboard />} />
              <Route path="team/members" element={<TeamMembersScreen />} />
              <Route path="team/invites" element={<TeamInvitesScreen />} />
              <Route path="team/permissions" element={<PermissionsScreen />} />
              <Route path="system/settings" element={<SystemSettings />} />
              <Route path="system/audit" element={<AuditLogsScreen />} />
              <Route path="system/health" element={<SystemHealthScreen />} />
              <Route path="settings" element={<SystemSettings />} />
              <Route index element={<Navigate to="/admin/dashboard" replace />} />
            </Route>

            {/* User App Routes */}
            {!isAuthenticated ? (
              <>
                <Route path="/" element={<LandingPage />} />
                <Route path="/auth" element={<AuthScreen />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </>
            ) : !hasCompletedOnboarding ? (
              <>
                <Route 
                  path="/onboarding" 
                  element={
                    <OnboardingFlow 
                      onComplete={() => setHasCompletedOnboarding(true)} 
                    />
                  } 
                />
                <Route path="*" element={<Navigate to="/onboarding" replace />} />
              </>
            ) : (
              <>
                <Route path="/" element={<Navigate to="/grid" replace />} />
                <Route path="/grid" element={
                  <div className="pb-16">
                    <GridScreen />
                    <Navigation />
                  </div>
                } />
                <Route path="/profile/:userId" element={<ProfileScreen />} />
                <Route path="/messages" element={
                  <div className="pb-16">
                    <MessagesScreen userRole={userRole} />
                    <Navigation />
                  </div>
                } />
                <Route path="/messages/chat/:userId" element={<ChatScreen />} />
                <Route path="/messages/:conversationId" element={<ChatScreen />} />
                <Route path="/cruise" element={
                  <div className="pb-16">
                    <CruiseScreen />
                    <Navigation />
                  </div>
                } />
                <Route path="/track" element={
                  <div className="pb-16">
                    <TrackScreen />
                    <Navigation />
                  </div>
                } />
                <Route path="/health" element={
                  <div className="pb-16">
                    <HealthScreen />
                    <Navigation />
                  </div>
                } />
                <Route path="/activity" element={
                  <div className="pb-16">
                    <ActivityScreen />
                    <Navigation />
                  </div>
                } />
                <Route path="/activity/log" element={<LogNewActivityScreen />} />
                <Route path="/settings" element={
                  <div className="pb-16">
                    <SettingsScreen />
                    <Navigation />
                  </div>
                } />
                <Route path="/settings/notifications" element={<NotificationsSettingsScreen />} />
                <Route path="/settings/privacy" element={<PrivacySecurityScreen />} />
                <Route path="/settings/blocked-users" element={<BlockedUsersScreen />} />
                <Route path="/settings/premium" element={<PremiumScreen />} />
                <Route path="/settings/payment" element={<PaymentSettingsScreen />} />
                <Route path="/settings/bugs-feedback" element={<BugsFeedbackScreen />} />
                <Route path="/settings/help" element={<HelpScreen />} />
                <Route path="/profile/edit" element={<EditProfileScreen />} />
                <Route path="*" element={<Navigate to="/grid" replace />} />
              </>
            )}
          </Routes>
        </Suspense>
        <PWAInstallBanner />
      </div>
    </BrowserRouter>
  );
};

function App() {
  return (
    <ErrorBoundary>
      <AuthProvider 
        domain={import.meta.env.VITE_AUTH0_DOMAIN || 'your-domain.auth0.com'}
        clientId={import.meta.env.VITE_AUTH0_CLIENT_ID || 'your-client-id'}
        redirectUri={window.location.origin}
      >
        <ThemeProvider>
          <AdminProvider>
            <UserPreferencesProvider>
              <NotificationProvider>
                <SubscriptionProvider initialTier={SubscriptionTier.FREE}>
                  <PaymentProvider>
                    <HealthProvider>
                      <ActivityProvider>
                        <FeatureProvider userRole={UserRoles.FREE} features={[]}>
                          <EnhancedAppProvider>
                            <ChatProvider currentUserId="current-user">
                              <SafetyProvider currentUserId="current-user">
                              <PerformanceProvider>
                                <PendingMatchesProvider>
                                  <AppContent />
                                </PendingMatchesProvider>
                              </PerformanceProvider>
                              </SafetyProvider>
                            </ChatProvider>
                          </EnhancedAppProvider>
                        </FeatureProvider>
                      </ActivityProvider>
                    </HealthProvider>
                  </PaymentProvider>
                </SubscriptionProvider>
              </NotificationProvider>
            </UserPreferencesProvider>
          </AdminProvider>
        </ThemeProvider>
      </AuthProvider>
    </ErrorBoundary>
  );
}

export default App;